import React, { useCallback, useContext, useEffect, useId, useRef, useState, } from 'react';
import { DatepickerContext } from './DatepickerContext';
import { SpinButton } from './SpinButton';
import { padZero } from './padZero';
import { Button } from '../button';
import { Calendar } from '../calendar';
import { getPaddedDateString, isDateInputWithTwoDigitYear, validateDate, } from '../util/dateUtil';
import debounce from 'lodash.debounce';
import classNames from 'classnames';
import { getSimpleDateFromString } from '../datelogic/simpledate';
import { ErrorFieldMessage } from '@sb1/ffe-form-react';
import i18n from '../i18n/i18n';
import { isMonth } from '../types';
export var DatepickerComp = function (_a) {
    var ariaInvalidState = _a.ariaInvalid, ariaInvalidProp = _a["aria-invalid"], ariaDescribedbyState = _a.ariaDescribedby, ariaDescribedbyProp = _a["aria-describedby"], onBlur = _a.onBlur, calendarAbove = _a.calendarAbove, id = _a.id, maxDateProp = _a.maxDate, minDateProp = _a.minDate, onChange = _a.onChange, fullWidth = _a.fullWidth, fieldMessage = _a.fieldMessage, labelId = _a.labelId, dropdownCaption = _a.dropdownCaption;
    var _b = useContext(DatepickerContext), day = _b.day, setDay = _b.setDay, year = _b.year, setYear = _b.setYear, month = _b.month, setMonth = _b.setMonth, locale = _b.locale, calendarActiveDate = _b.calendarActiveDate, setCalendarActiveDate = _b.setCalendarActiveDate, setLastChangedValue = _b.setLastChangedValue;
    var formatDate = useCallback(function () {
        return getPaddedDateString(day, month, year);
    }, [day, month, year]);
    var _c = useState(false), displayDatePicker = _c[0], setDisplayDatePicker = _c[1];
    var _d = useState(minDateProp), minDate = _d[0], setMinDate = _d[1];
    var _e = useState(maxDateProp), maxDate = _e[0], setMaxDate = _e[1];
    var _f = useState(formatDate()), lastValidDate = _f[0], setLastValidDate = _f[1];
    var datepickerId = useId();
    var buttonRef = useRef(null);
    var dayRef = useRef(null);
    var monthRef = useRef(null);
    var yearRef = useRef(null);
    var getFieldMessageId = function () {
        return fieldMessage ? "".concat(datepickerId, "-fieldmessage") : undefined;
    };
    var _onChange = useCallback(function (date) {
        setLastChangedValue(date);
        onChange(date);
    }, [onChange, setLastChangedValue]);
    var fieldMessageId = getFieldMessageId();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    var debounceCalendar = useCallback(debounce(function (newValue) {
        if (newValue !== lastValidDate && validateDate(newValue)) {
            setCalendarActiveDate(newValue);
            setLastValidDate(newValue);
        }
    }, 250), [lastValidDate]);
    var hasMessage = !!fieldMessage;
    useEffect(function () {
        return function () {
            debounceCalendar.cancel();
        };
    }, [debounceCalendar]);
    var validateDateIntervals = useCallback(function () {
        var dateString = formatDate();
        getSimpleDateFromString(dateString, function (date) {
            var maxDateValidated = maxDateProp
                ? getSimpleDateFromString(maxDateProp)
                : null;
            if ((maxDateValidated === null || maxDateValidated === void 0 ? void 0 : maxDateValidated.isBefore(date)) &&
                isDateInputWithTwoDigitYear(dateString)) {
                date.adjust({ period: 'Y', offset: -100 });
            }
            var formattedDate = date.format();
            if (formattedDate !== dateString) {
                setDay([date.date]);
                setMonth([date.month + 1]);
                setYear([date.year]);
            }
            setLastValidDate(formattedDate);
        });
    }, [formatDate, maxDateProp, setDay, setMonth, setYear]);
    useEffect(function () {
        if (minDateProp !== minDate || maxDateProp !== maxDate) {
            setMinDate(minDateProp);
            setMaxDate(maxDateProp);
            validateDateIntervals();
        }
        debounceCalendar(formatDate());
    }, [
        minDateProp,
        maxDateProp,
        formatDate,
        debounceCalendar,
        minDate,
        maxDate,
        validateDateIntervals,
    ]);
    var closeCalendarSetInputFocus = function () {
        var _a;
        setDisplayDatePicker(false);
        (_a = buttonRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        validateDateIntervals();
    };
    var escKeyHandler = function (evt) {
        if (evt.key === 'Escape') {
            closeCalendarSetInputFocus();
        }
    };
    var globalClickHandler = function (evt) {
        if (displayDatePicker && evt.__datepickerID !== datepickerId) {
            closeCalendarSetInputFocus();
        }
    };
    var addGlobalEventListeners = function () {
        window.addEventListener('click', globalClickHandler);
        window.addEventListener('keyup', escKeyHandler);
    };
    var removeGlobalEventListeners = function () {
        window.removeEventListener('click', globalClickHandler);
        window.removeEventListener('keyup', escKeyHandler);
    };
    useEffect(function () {
        if (displayDatePicker) {
            addGlobalEventListeners();
        }
        else {
            removeGlobalEventListeners();
        }
        return function () {
            removeGlobalEventListeners();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [displayDatePicker]);
    var calendarButtonClickHandler = function () {
        validateDateIntervals();
        setDisplayDatePicker(!displayDatePicker);
    };
    /**
     * Adds a flag on the click event so that the globalClickHandler()
     * can determine whether the ID matches. Makes it so that only one datepicker can be open at the same time
     */
    var addFlagOnClickEventClickHandler = function (evt) {
        var nativeEvent = evt.nativeEvent;
        nativeEvent.__datepickerID = datepickerId;
    };
    var focusSpinButton = function (evt) {
        var _a;
        if (evt.target !== yearRef.current &&
            evt.target !== buttonRef.current &&
            evt.target !== dayRef.current &&
            evt.target !== monthRef.current) {
            (_a = dayRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
    };
    var datePickedHandler = function (date) {
        var _a;
        var simpleDate = getSimpleDateFromString(date);
        if (simpleDate) {
            setDay([simpleDate.date]);
            setMonth([simpleDate.month + 1]);
            setYear([simpleDate.year]);
            setDisplayDatePicker(false);
            setCalendarActiveDate(date);
            (_a = buttonRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
    };
    var ariaInvalid = function () {
        var ariaInvalidTotal = ariaInvalidProp === 'true' || ariaInvalidState;
        var isAriaInvalid = ariaInvalidTotal === 'true' || ariaInvalidTotal === true
            ? 'true'
            : undefined;
        return isAriaInvalid;
    };
    var ariaDescribedby = function () {
        var ariaDescribedbyTotal = ariaDescribedbyProp !== null && ariaDescribedbyProp !== void 0 ? ariaDescribedbyProp : ariaDescribedbyState;
        return ariaDescribedbyTotal;
    };
    var handlePaste = function (evt) {
        evt.preventDefault();
        var paste = (evt.clipboardData || window.Clipboard).getData('text');
        var date = getSimpleDateFromString(paste);
        if (date) {
            setDay([date.date]);
            setMonth([date.month + 1]);
            setYear([date.year]);
        }
    };
    var _g = useState(true), init = _g[0], setInit = _g[1];
    useEffect(function () {
        if (init) {
            setInit(false);
            return;
        }
        var _a = lastValidDate.split('.').map(Number), day = _a[0], month = _a[1], year = _a[2];
        if (day * month * year > 0) {
            _onChange(lastValidDate);
            return;
        }
        _onChange('');
    }, [lastValidDate]);
    return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions,jsx-a11y/no-noninteractive-element-interactions
    React.createElement("div", { className: classNames('ffe-datepicker', 'ffe-default-mode', {
            'ffe-datepicker--full-width': fullWidth,
            'ffe-input-group--message': hasMessage,
            'ffe-datepicker--invalid': ariaInvalid(),
        }), "data-testid": "date-picker", onClick: function (e) {
            addFlagOnClickEventClickHandler(e);
            focusSpinButton(e);
        }, role: 'group', id: id },
        React.createElement("div", { className: classNames('ffe-input-field', 'ffe-dateinput', {
                'ffe-input-field--invalid': ariaInvalid(),
            }), onBlur: function (evt) {
                var elementReceivingFocus = evt.relatedTarget;
                if (elementReceivingFocus !== yearRef.current &&
                    elementReceivingFocus !== dayRef.current &&
                    elementReceivingFocus !== monthRef.current) {
                    onBlur === null || onBlur === void 0 ? void 0 : onBlur(evt);
                    validateDateIntervals();
                }
            } },
            React.createElement(SpinButton, { ref: dayRef, value: day !== null && day !== void 0 ? day : undefined, min: 1, max: 31, onPaste: handlePaste, onSpinButtonChange: function (newValue, allowFocusNext) {
                    if (allowFocusNext === void 0) { allowFocusNext = true; }
                    return allowFocusNext
                        ? setDay(newValue, function () {
                            var _a;
                            return (_a = monthRef.current) === null || _a === void 0 ? void 0 : _a.focus({
                                preventScroll: true,
                            });
                        })
                        : setDay(newValue);
                }, nextSpinButton: monthRef, maxLength: 2, "aria-invalid": ariaInvalid(), "aria-valuenow": typeof day === 'number' ? day : undefined, "aria-valuetext": "".concat(day), "aria-label": i18n[locale].DAY, "aria-describedby": ariaDescribedby(), "aria-labelledby": labelId }, day ? padZero(day) : 'dd'),
            ".",
            React.createElement(SpinButton, { ref: monthRef, value: month !== null && month !== void 0 ? month : undefined, min: 1, max: 12, onPaste: handlePaste, onSpinButtonChange: function (newValue, allowFocusNext) {
                    if (allowFocusNext === void 0) { allowFocusNext = true; }
                    return allowFocusNext
                        ? setMonth(newValue, function () {
                            var _a;
                            return (_a = yearRef.current) === null || _a === void 0 ? void 0 : _a.focus({
                                preventScroll: true,
                            });
                        })
                        : setMonth(newValue);
                }, nextSpinButton: yearRef, prevSpinButton: dayRef, maxLength: 2, "aria-invalid": ariaInvalid(), "aria-valuenow": typeof month === 'number' ? month : undefined, "aria-valuetext": isMonth(month)
                    ? "".concat(month, " - ").concat(i18n[locale]["MONTH_".concat(month)])
                    : undefined, "aria-label": i18n[locale].MONTH, "aria-describedby": ariaDescribedby(), "aria-labelledby": labelId }, month ? padZero(month) : 'mm'),
            ".",
            React.createElement(SpinButton, { ref: yearRef, className: 'ffe-dateinput__field-year', value: year !== null && year !== void 0 ? year : undefined, min: 1, max: 9999, onPaste: handlePaste, onSpinButtonChange: function (newValue) {
                    setYear(newValue);
                }, prevSpinButton: monthRef, maxLength: 4, "aria-invalid": ariaInvalid(), "aria-valuetext": "".concat(year), "aria-valuenow": typeof year === 'number' ? year : undefined, "aria-label": i18n[locale].YEAR, "aria-describedby": ariaDescribedby(), "aria-labelledby": labelId }, year ? year : 'yyyy')),
        React.createElement(Button, { onClick: calendarButtonClickHandler, locale: locale, value: calendarActiveDate || '', ref: buttonRef }),
        displayDatePicker && (React.createElement(Calendar, { calendarClassName: classNames('ffe-calendar ffe-calendar--datepicker', { 'ffe-calendar--datepicker--above': calendarAbove }), locale: locale, onDatePicked: datePickedHandler, selectedDate: calendarActiveDate, focusOnMount: true, dropdownCaption: dropdownCaption, minDate: minDate, maxDate: maxDate })),
        !!fieldMessage && (React.createElement(ErrorFieldMessage, { as: "p", id: fieldMessageId }, fieldMessage))));
};
